import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import React, { FormEventHandler, useState } from 'react';
import { t } from 'i18next';
import { ACCORDION_STYLES } from '../../../common/components/IntegrationAccordion';
import { useFormItem } from '../../../common/hooks/use-form-item';
import { InputType } from '../../../common/hooks/use-form-item/types';
import { and } from '../../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../../common/hooks/use-form-item/validators/operational';
import { CrowdStrike } from '../../../integrations/crowdstrike/models/CrowdStrike';
import { CheckCrowdStrikeIntegrationArgs, CrowdStrikeIntegrationArgs } from '../../../integrations/crowdstrike/services/CrowdStrikeRepository';
import { DangerButton, PrimaryButton } from '../../../modal-flows/components/Buttons';
import { url, urlWithoutTrailingSlash } from '../../../common/hooks/use-form-item/validators/type';
import { Visibility, VisibilityOff } from '@mui/icons-material';

type Props = {
  crowdStrike: CrowdStrike | null;
  onTestClick: (args: CheckCrowdStrikeIntegrationArgs) => void;
  onConnectClick: (args: CrowdStrikeIntegrationArgs) => void;
  onUnlinkClick: () => void;
  canUpdate: boolean;
};

export const CrowdStrikeAccordionForm = (props: Props) => {
  const { crowdStrike, onConnectClick, onTestClick, canUpdate, onUnlinkClick } = props;
  const isCrowdStrikeConfigured = Boolean(crowdStrike);
  const [visible, setVisible] = useState<boolean>(false);

  const [baseUrl, baseUrlComponent, isBaseUrlValid] = useFormItem({
    type: InputType.TYPE_TEXT,
    value: crowdStrike?.getBaseUrl() ?? '',
    validator: and(required(), url(), urlWithoutTrailingSlash(), maxLength(1000)),
    inputOptions: {
      autoFocus: true,
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    label: t('integrationList.crowdstrike.baseUrlLabel'),
  });
  const [clientId, clientIdComponent, isClientIdValid] = useFormItem({
    type: InputType.TYPE_TEXT,
    value: crowdStrike?.getClientId() ?? '',
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    label: t('integrationList.crowdstrike.clientIdLabel'),
  });
  const [clientSecret, clientSecretComponent, isClientSecretValid] = useFormItem({
    type: visible ? InputType.TYPE_TEXT : InputType.TYPE_PASSWORD,
    value: crowdStrike?.getClientSecret() ?? '',
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    label: t('integrationList.crowdstrike.clientSecretLabel'),
  });

  const isFormValid = isBaseUrlValid && isClientIdValid && isClientSecretValid;
  const fieldsModified = baseUrl !== crowdStrike?.getBaseUrl() || clientId !== crowdStrike?.getClientId() || clientSecret !== crowdStrike.getClientSecret();
  const canSubmit = isFormValid && fieldsModified;

  const handleConnectButtonClick: FormEventHandler = (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    onConnectClick({
      baseUrl,
      clientId,
      clientSecret,
    });
  };

  const handleTestClick = () => {
    if (!isFormValid) return;

    onTestClick({
      baseUrl,
      clientId,
      clientSecret,
    });
  };

  const toggleVisibility = () => {
    setVisible((prevValue) => !prevValue);
  };

  return (
    <Box sx={{ p: 3, backgroundImage: ACCORDION_STYLES.backgroundImage }}>
      <Stack spacing={1.5} component="form" onSubmit={handleConnectButtonClick}>
        {baseUrlComponent}
        {clientIdComponent}
        <Stack direction="row" spacing={1} alignItems="end">
          {clientSecretComponent}
          <IconButton
            data-role="access-token-toggle"
            size="small"
            title={t(visible ? 'toggleVisibility.visible' : 'toggleVisibility.hidden', { field: t('integrationList.crowdstrike.clientSecretLabel') })}
            disabled={!clientSecret}
            onClick={toggleVisibility}
          >
            {visible ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Stack>
        <Divider />
        <Stack direction="row" gap={2}>
          {canUpdate ? (
            <>
              {isCrowdStrikeConfigured && <DangerButton onClick={onUnlinkClick}>{t('integrationList.crowdstrike.unlinkButton')}</DangerButton>}
              <PrimaryButton disabled={!isFormValid} onClick={handleTestClick} sx={{ ml: 'auto' }}>
                {t('integrationList.crowdstrike.testButton')}
              </PrimaryButton>
              <PrimaryButton disabled={!canSubmit} type="submit">
                {t('integrationList.crowdstrike.connectButton')}
              </PrimaryButton>
            </>
          ) : (
            <Typography>{t('integrationList.noPermission')}</Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
